import React, { useState, useEffect } from 'react';
import './AssignCreatorModal.css';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { collection, onSnapshot, doc, updateDoc, arrayUnion, getDoc, setDoc, getDocs } from 'firebase/firestore';
import NotificationService from './NotificationService';
import { ProjectIdGenerator } from '../utils/projectIdGenerator';
import { addBusinessDays } from '../utils/dateUtils';
import ReviewContentModal from './ReviewContentModal';
import CreditService from '../services/creditService';
import CreditPurchaseModal from './CreditPurchaseModal';
import CreatorAssignmentConfirmationModal from './CreatorAssignmentConfirmationModal';

function AssignCreatorModal({ 
  isOpen, 
  onClose, 
  creators = [], 
  onRemoveCreator,
  preselectCampaign = '',
  preselectBrief = null,
  preselectScript = null,
  onAssignComplete 
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCampaign, setSelectedCampaign] = useState(preselectCampaign);
  const [campaigns, setCampaigns] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [campaignsWithBriefs, setCampaignsWithBriefs] = useState([]);
  const [selectedBriefs, setSelectedBriefs] = useState({}); // { [creatorHandle]: briefId }
  const [selectedScripts, setSelectedScripts] = useState({}); // { [creatorHandle]: { [briefId]: [scriptId1, scriptId2, ...] } }
  const [scriptDropdownValues, setScriptDropdownValues] = useState({}); // { [creatorHandle]: { [briefId]: '' } }
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [freshCreators, setFreshCreators] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewType, setReviewType] = useState(null); // 'brief' or 'script'
  const [reviewContent, setReviewContent] = useState(null);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [currentCredits, setCurrentCredits] = useState(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [assignmentData, setAssignmentData] = useState([]);

  // Fetch current credits when modal opens
  useEffect(() => {
    if (isOpen) {
      CreditService.getBalance()
        .then(setCurrentCredits)
        .catch(error => {
          console.error('Error fetching credits:', error);
          setCurrentCredits(0);
        });
    }
  }, [isOpen]);

  // Fetch fresh creator data from Firestore when modal opens
  useEffect(() => {
    if (!isOpen || creators.length === 0) {
      setFreshCreators([]);
      return;
    }

    const fetchFreshCreatorData = async () => {
      try {
        const freshData = await Promise.all(
          creators.map(async (creator) => {
            // Try to get creator ID from various possible fields
            const creatorId = creator.id || creator.uid || (creator.handle ? creator.handle.replace('@', '') : null);
            
            if (!creatorId) {
              // If no ID, return original creator data
              return creator;
            }

            try {
              const creatorDoc = await getDoc(doc(db, 'users', creatorId));
              if (creatorDoc.exists()) {
                const creatorData = creatorDoc.data();
                // Merge fresh data with existing creator data, prioritizing fresh data
                // Ensure handle has @ prefix (remove existing @ if present, then add it)
                const normalizeHandle = (handle) => {
                  if (!handle) return '';
                  const cleaned = handle.replace(/^@+/, ''); // Remove any leading @ symbols
                  return cleaned ? `@${cleaned}` : '';
                };
                
                const freshHandle = creatorData.handle || creator.handle;
                const normalizedHandle = normalizeHandle(freshHandle);
                
                return {
                  ...creator,
                  name: creatorData.name || creatorData.displayName || creator.name,
                  handle: normalizedHandle || creator.handle,
                  profileImage: creatorData.profileImage || creatorData.profileImagePath || creatorData.avatar || creatorData.photoURL || creator.profileImage || creator.image,
                  image: creatorData.profileImage || creatorData.profileImagePath || creatorData.avatar || creatorData.photoURL || creator.image || creator.profileImage,
                  age: creatorData.age || creatorData.ageRange || creator.age,
                  gender: creatorData.gender || creator.gender,
                  niches: creatorData.niches || creator.niches,
                  niche: creatorData.niches && creatorData.niches.length > 0 ? creatorData.niches.join('.') : creator.niche,
                  bio: creatorData.bio || creatorData.about || creator.bio,
                  hobbies: creatorData.hobbies || creator.hobbies,
                };
              }
            } catch (error) {
              console.debug('Could not fetch fresh creator data for:', creatorId, error.message);
              // Return original creator data if fetch fails
              return creator;
            }
            
            return creator;
          })
        );
        
        setFreshCreators(freshData);
      } catch (error) {
        console.error('Error fetching fresh creator data:', error);
        // Fallback to original creators if fetch fails
        setFreshCreators(creators);
      }
    };

    fetchFreshCreatorData();
  }, [isOpen, creators]);

  // Use fresh creators if available, otherwise fall back to original creators
  const displayCreators = freshCreators.length > 0 ? freshCreators : creators;

  // Helper function to format handle for display (ensures single @ prefix)
  const formatHandleForDisplay = (handle) => {
    if (!handle) return '';
    // Remove any leading @ symbols and add exactly one
    const cleaned = handle.replace(/^@+/, '');
    return cleaned ? `@${cleaned}` : '';
  };

  const steps = [
    { number: 1, title: 'Select Campaign', description: 'Choose which campaign to assign creators to' },
    { number: 2, title: 'Choose Briefs', description: 'Select briefs for each creator' },
    { number: 3, title: 'Pick Scripts', description: 'Choose scripts under each brief' },
    { number: 4, title: 'Review & Assign', description: 'Review selections and assign creators' }
  ];

  // Check if campaign has briefs
  const checkCampaignHasBriefs = async (campaignId) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return false;

    try {
      const briefsRef = collection(db, 'users', user.uid, 'campaigns', campaignId, 'briefs');
      const snapshot = await getDocs(briefsRef);
      const hasBriefs = snapshot.docs.length > 0;
      console.log(`Campaign ${campaignId} has ${snapshot.docs.length} briefs:`, hasBriefs);
      return hasBriefs;
    } catch (error) {
      console.error('Error checking campaign briefs:', error);
      return false;
    }
  };

  // Fetch campaigns from Firestore
  useEffect(() => {
    if (!isOpen) return;
    
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
    const unsubscribe = onSnapshot(campaignsRef, async (snapshot) => {
      const fetchedCampaigns = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.id,
        ...doc.data()
      }));
      setCampaigns(fetchedCampaigns);
      console.log('Fetched campaigns:', fetchedCampaigns);

      // Filter campaigns that have briefs
      const campaignsWithBriefsList = [];
      for (const campaign of fetchedCampaigns) {
        const hasBriefs = await checkCampaignHasBriefs(campaign.id);
        if (hasBriefs) {
          campaignsWithBriefsList.push(campaign);
        }
      }
      setCampaignsWithBriefs(campaignsWithBriefsList);
    });

    return () => unsubscribe();
  }, [isOpen]);

  // Fetch briefs for selected campaign
  useEffect(() => {
    if (!selectedCampaign || !isOpen) {
      setBriefs([]);
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const briefsRef = collection(db, 'users', user.uid, 'campaigns', selectedCampaign, 'briefs');
    const unsubscribe = onSnapshot(briefsRef, (snapshot) => {
      const fetchedBriefs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBriefs(fetchedBriefs);
    });

    return () => unsubscribe();
  }, [selectedCampaign, isOpen]);


  // Debug briefScripts structure
  useEffect(() => {
    if (briefs.length > 0) {
      console.log('Briefs structure:', briefs.map(b => ({
        id: b.id,
        title: b.title,
        name: b.name,
        scripts: b.scripts?.map(s => ({ id: s.id, name: s.name, title: s.title })) || []
      })));
    }
  }, [briefs]);

  // Initialize with preselected values
  useEffect(() => {
    if (preselectCampaign) {
      setSelectedCampaign(preselectCampaign);
      // Skip to step 2 if campaign is preselected
      setCurrentStep(2);
    }
    const creatorsToUse = displayCreators.length > 0 ? displayCreators : creators;
    if (preselectBrief && creatorsToUse.length > 0) {
      creatorsToUse.forEach(creator => {
        setSelectedBriefs(prev => ({
          ...prev,
          [creator.handle]: preselectBrief.id || preselectBrief
        }));
      });
    }
    if (preselectScript && preselectBrief && creatorsToUse.length > 0) {
      creatorsToUse.forEach(creator => {
        setSelectedScripts(prev => ({
          ...prev,
          [creator.handle]: {
            ...prev[creator.handle],
            [preselectBrief.id || preselectBrief]: preselectScript.id || preselectScript
          }
        }));
      });
    }
  }, [preselectCampaign, preselectBrief, preselectScript, creators, displayCreators]);

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleRemoveCreator = (creatorHandle) => {
    if (onRemoveCreator) {
      const creatorToRemove = displayCreators.find(c => c.handle === creatorHandle);
      if (creatorToRemove) {
        onRemoveCreator(creatorToRemove);
      }
    }
  };

  const handleBriefChange = (creatorHandle, briefId) => {
    setSelectedBriefs(prev => ({
      ...prev,
      [creatorHandle]: briefId
    }));
    
    // Clear scripts when brief changes
    setSelectedScripts(prev => ({
      ...prev,
      [creatorHandle]: {}
    }));
  };

  const handleScriptChange = (creatorHandle, briefId, scriptId) => {
    if (!scriptId) return;
    
    console.log('Adding script:', { creatorHandle, briefId, scriptId });
    
    setSelectedScripts(prev => {
      const newState = {
        ...prev,
        [creatorHandle]: {
          ...prev[creatorHandle],
          [briefId]: [...(prev[creatorHandle]?.[briefId] || []), scriptId]
        }
      };
      console.log('New selectedScripts state:', newState);
      return newState;
    });
    
    // Reset the dropdown value
    setScriptDropdownValues(prev => ({
      ...prev,
      [creatorHandle]: {
        ...prev[creatorHandle],
        [briefId]: ''
      }
    }));
  };

  const handleRemoveScript = (creatorHandle, briefId, scriptId) => {
    setSelectedScripts(prev => ({
      ...prev,
      [creatorHandle]: {
        ...prev[creatorHandle],
        [briefId]: (prev[creatorHandle]?.[briefId] || []).filter(id => id !== scriptId)
      }
    }));
  };

  const getBriefScripts = (briefId) => {
    // Scripts are stored within brief documents as brief.scripts array
    // Filter out draft and non-approved scripts - only return scripts brand can assign to creators
    const brief = briefs.find(b => b.id === briefId);
    const allScripts = brief?.scripts || [];
    return allScripts.filter(script => {
      if (script.isDraft) return false;
      if (script.approvalStatus === 'pending_brand_approval' || script.approvalStatus === 'rejected') return false;
      return true; // approved or no approvalStatus (legacy/brand-created)
    });
  };

  // Calculate pricing for additional hooks and CTAs
  const calculateScriptPricing = (script, creatorTier = 'Rising Star') => {
    if (!script || !script.lines) {
      const basePrice = creatorTier === 'Pro' ? 1 : 0.5;
      return { basePrice, extras: 0, total: basePrice };
    }
    
    let extraHooks = 0;
    let extraCTAs = 0;
    
    script.lines.forEach((line, idx) => {
      if (line.type === 'hook' && idx !== 0) extraHooks++;
      if (line.type === 'cta' && idx !== script.lines.length - 1) extraCTAs++;
    });
    
    const basePrice = creatorTier === 'Pro' ? 1 : 0.5; // Pro: 1 credit, Rising Star: 0.5 credits
    const extras = (extraHooks + extraCTAs) * 0.25; // 0.25 credits per extra hook/CTA
    const total = basePrice + extras;
    
    return { basePrice, extras, total, extraHooks, extraCTAs };
  };

  // Calculate total pricing for all assignments
  const calculateTotalPricing = () => {
    let totalCredits = 0;
    let totalExtras = 0;
    
    creators.forEach(creator => {
      const selectedBriefId = selectedBriefs[creator.handle];
      const selectedScriptIds = selectedScripts[creator.handle]?.[selectedBriefId];
      
      if (selectedBriefId && selectedBriefId !== '' && selectedScriptIds && selectedScriptIds.length > 0) {
        const selectedBrief = briefs.find(b => b.id === selectedBriefId);
        
        selectedScriptIds.forEach(scriptId => {
          const selectedScript = selectedBrief?.scripts?.find(s => s.id === scriptId);
          
          if (selectedScript) {
            const pricing = calculateScriptPricing(selectedScript, creator.tier);
            totalCredits += pricing.total;
            totalExtras += pricing.extras;
          }
        });
      }
    });
    
    return { totalCredits, totalExtras };
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return selectedCampaign && campaignsWithBriefs.length > 0;
      case 2:
        return Object.keys(selectedBriefs).length === creators.length && 
               Object.values(selectedBriefs).every(briefId => briefId && briefId !== '');
      case 3:
        return Object.keys(selectedScripts).length === creators.length && 
               Object.values(selectedScripts).every(creatorScripts => 
                 Object.values(creatorScripts || {}).every(scriptArray => 
                   Array.isArray(scriptArray) && scriptArray.length > 0
                 )
               );
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleFinalAssign = async () => {
    setIsLoading(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;

      // Calculate total credits needed
      const { totalCredits, totalExtras } = calculateTotalPricing();
      // totalCredits already includes extras, so we don't need to add totalExtras again
      const totalCreditsNeeded = totalCredits;

      // Check if user has sufficient credits
      const creditCheck = await CreditService.checkSufficientCredits(totalCreditsNeeded);
      
      if (!creditCheck.sufficient) {
        // Update current credits and open credit purchase modal
        setCurrentCredits(creditCheck.currentBalance);
        setShowCreditPurchaseModal(true);
        setIsLoading(false);
        return;
      }

      // Deduct credits before assignment
      try {
        await CreditService.deductCredits(
          totalCreditsNeeded,
          `Assignment: ${creators.length} creator(s) to campaign`,
          {
            campaignId: selectedCampaign,
            creatorCount: creators.length,
            scriptCount: Object.values(selectedScripts).reduce((sum, scripts) => {
              return sum + Object.values(scripts || {}).reduce((s, arr) => s + (arr?.length || 0), 0);
            }, 0)
          }
        );
        console.log(`✅ Deducted ${totalCreditsNeeded.toFixed(2)} credits for assignment`);
      } catch (creditError) {
        console.error('Error deducting credits:', creditError);
        setToast(`Failed to deduct credits: ${creditError.message}. Assignment cancelled.`);
        setIsLoading(false);
        return;
      }

      // Create assignments for each creator
      const assignments = displayCreators.map(creator => {
        const creatorBriefs = selectedBriefs[creator.handle] ? [selectedBriefs[creator.handle]] : [];
        const creatorScripts = selectedScripts[creator.handle] || {};
        
        return {
          creatorHandle: creator.handle,
          assignments: creatorBriefs.map(briefId => ({
            briefId,
            scriptId: creatorScripts[briefId] || null
          }))
        };
      });

      // Update Firestore - Add creators to briefs/scripts and remove from shortlist
      const campaignRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign);
      const campaignDoc = await getDoc(campaignRef);
      const campaignData = campaignDoc.exists() ? campaignDoc.data() : null;
      
      // Store projectIds to reuse for notifications (ensures same ID for assignment and notification)
      // Declared outside if block so it's accessible when sending notifications
      const creatorProjectIds = {}; // { [creatorId]: { [briefId]: projectId } }
      
      if (campaignDoc.exists()) {
        let updatedShortlistedCreators = campaignData.creators || [];

        // Update briefs to include assigned creators and remove from shortlist
        for (const creator of creators) {
          const creatorBriefs = selectedBriefs[creator.handle] ? [selectedBriefs[creator.handle]] : [];
          const creatorScripts = selectedScripts[creator.handle] || {};
          
          for (const briefId of creatorBriefs) {
            const briefRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign, 'briefs', briefId);
            const briefDoc = await getDoc(briefRef);
            
            if (briefDoc.exists()) {
              const briefData = briefDoc.data();
              const existingBriefCreators = briefData.creators || [];
              const existingCreatorIds = briefData.creatorIds || [];

              // Allow multiple assignments per creator to the same brief
                // Calculate default deadline (7 business days from now, excluding weekends)
                const assignedDate = new Date();
                const defaultDeadline = addBusinessDays(assignedDate, 7);
                
                // Get productType from the assigned script and create script snapshot
                let productType = 'service'; // Default fallback
                const assignedScriptId = creatorScripts[briefId];
                let assignedScriptsSnapshot = null;
                
                // IMPORTANT: Create snapshot of assigned scripts at assignment time
                // This ensures scripts don't change if brand updates the brief later
                if (briefData.scripts) {
                  const scriptIds = Array.isArray(assignedScriptId) ? assignedScriptId : (assignedScriptId ? [assignedScriptId] : []);
                  
                  // Filter assigned scripts
                  const assignedScripts = briefData.scripts.filter(script => 
                    scriptIds.includes(script.id) || scriptIds.includes(String(script.id))
                  );
                  
                  // Create deep copy snapshot
                  if (assignedScripts.length > 0) {
                    assignedScriptsSnapshot = JSON.parse(JSON.stringify(assignedScripts));
                    console.log('📸 Created script snapshot in brief assignment:', {
                      scriptIds,
                      snapshotCount: assignedScriptsSnapshot.length
                    });
                    
                    // Get productType from the first assigned script
                    const assignedScript = assignedScripts[0];
                    if (assignedScript && assignedScript.settings && assignedScript.settings.productType) {
                      productType = assignedScript.settings.productType;
                    }
                  }
                }

                // Generate unique project ID for this assignment
                const projectId = ProjectIdGenerator.generateProjectId(
                  String(user.uid).trim(), // brandId
                  String(selectedCampaign).trim(), // campaignId
                  String(briefId).trim(), // briefId
                  String(creator.id).trim() // creatorId
                );

                const creatorAssignment = {
                  id: creator.id,
                  projectId: projectId, // NEW: Unique project identifier
                  name: creator.name,
                  handle: creator.handle,
                  avatar: creator.avatar || creator.profileImage || '/creatorimages/creatorpic1v2-min.png',
                  tier: creator.tier || 'Rising Star',
                  status: 'awaiting_approval',
                  assignedAt: new Date().toISOString(),
                  // Service work starts the 7-day clock immediately. For products,
                  // the clock must not run until the creator receives the product,
                  // so leave the deadline unset here — it's set on product receipt.
                  deadline: productType.toLowerCase() === 'product' ? null : defaultDeadline.toISOString(),
                  scriptIds: creatorScripts[briefId] || [],
                  assignedScriptsSnapshot: assignedScriptsSnapshot, // Store snapshot of scripts at assignment time
                  productType: productType
                };
                
                // Update both creators array and creatorIds array
                await updateDoc(briefRef, {
                  creators: arrayUnion(creatorAssignment),
                  creatorIds: arrayUnion(creator.id)
                });
              
                // Store projectId to reuse for notification (ensures same ID)
                if (!creatorProjectIds[creator.id]) {
                  creatorProjectIds[creator.id] = {};
                }
                creatorProjectIds[creator.id][briefId] = projectId;
            }
          }

          // Remove creator from campaign shortlist since they're now assigned
          updatedShortlistedCreators = updatedShortlistedCreators.filter(c => c.id !== creator.id);
        }

        // Update the campaign's shortlisted creators (remove assigned ones)
        await updateDoc(campaignRef, {
          creators: updatedShortlistedCreators
        });
      }

      // Send notifications to creators
      try {
        for (const creator of creators) {
          const creatorBriefs = selectedBriefs[creator.handle] ? [selectedBriefs[creator.handle]] : [];
          const creatorScripts = selectedScripts[creator.handle] || {};
          
          for (const briefId of creatorBriefs) {
            const brief = briefs.find(b => b.id === briefId);
            const scriptIds = creatorScripts[briefId] || [];
            
            // Get productType from the assigned script
            let productType = 'service'; // Default fallback
            let creativeControl = null;
            let platform = null;
            const assignedScriptId = scriptIds[0]; // Get first script ID
            if (assignedScriptId && brief.scripts) {
              const assignedScript = brief.scripts.find(s => s.id === assignedScriptId);
              if (assignedScript && assignedScript.settings && assignedScript.settings.productType) {
                productType = assignedScript.settings.productType;
              }
              if (assignedScript && assignedScript.settings && assignedScript.settings.creativeControl) {
                creativeControl = assignedScript.settings.creativeControl;
              }
              if (assignedScript && assignedScript.settings && assignedScript.settings.platform) {
                platform = assignedScript.settings.platform;
              } else if (assignedScript && assignedScript.platform) {
                platform = assignedScript.platform;
              }
            }
            if (!creativeControl && brief?.briefSettings?.creativeControl) {
              creativeControl = brief.briefSettings.creativeControl;
            }
            if (!platform && brief?.briefSettings?.platform) {
              platform = brief.briefSettings.platform;
            }
            if (!platform && campaignData?.defaultPlatform) {
              platform = campaignData.defaultPlatform;
            }
            
            // REUSE the projectId from the creator assignment (generated above)
            // This ensures the notification and brief entry have the SAME projectId
            // CRITICAL: If projectId wasn't stored, this is a bug - throw error instead of generating new one
            const projectId = creatorProjectIds?.[creator.id]?.[briefId];
            
            if (!projectId) {
              console.error(`❌ CRITICAL: ProjectId not found in stored map for creator ${creator.id}, brief ${briefId}. This should never happen.`);
              throw new Error(`Failed to retrieve projectId for creator assignment. Please try again.`);
            }

            const senderDoc = await getDoc(doc(db, 'users', user.uid));
            await NotificationService.notifyCreatorAssignment(creator.id, {
              projectId: projectId, // NEW: Include project ID
              campaignId: selectedCampaign,
              campaignName: campaignData?.name || selectedCampaign,
              briefId,
              briefTitle: brief?.title || brief?.name || 'Untitled Brief',
              scriptIds,
              productType: productType,
              brandAvatarImage: campaignData?.avatarType === 'image' ? campaignData?.avatarImage : null,
              brandAvatarColor: campaignData?.avatarType === 'color' ? campaignData?.avatarColor : null,
              brandName: senderDoc.data()?.brandName || senderDoc.data()?.displayName || senderDoc.data()?.companyName || senderDoc.data()?.agencyName || senderDoc.data()?.email || campaignData?.name || 'Brand',
              creativeControl: creativeControl || brief?.briefSettings?.creativeControl || 'exact',
              platform: platform || campaignData?.platform || 'tiktok'
            });
          }
        }
        console.log('All creator notifications sent successfully');
      } catch (notificationError) {
        console.error('Error sending notifications:', notificationError);
        // Don't fail the assignment if notifications fail
      }

      // Build assignment data for confirmation modal
      const confirmationData = displayCreators.map(creator => {
        const creatorBriefId = selectedBriefs[creator.handle];
        const creatorScriptIds = selectedScripts[creator.handle]?.[creatorBriefId];
        // Handle both array and single value for script IDs
        const scriptIdsArray = Array.isArray(creatorScriptIds) 
          ? creatorScriptIds 
          : (creatorScriptIds ? [creatorScriptIds] : []);
        const brief = briefs.find(b => b.id === creatorBriefId);
        const assignedScripts = brief?.scripts?.filter(s => {
          // Compare both as strings and numbers to handle type mismatches
          return scriptIdsArray.includes(s.id) || 
                 scriptIdsArray.includes(String(s.id)) || 
                 scriptIdsArray.includes(Number(s.id));
        }) || [];
        
        return {
          creator: {
            id: creator.id,
            name: creator.name,
            handle: creator.handle,
            avatar: creator.profileImage || creator.image || creator.avatar,
          },
          assignments: [{
            brief: brief ? {
              id: brief.id,
              title: brief.title || brief.name,
            } : null,
            scripts: assignedScripts.map(script => ({
              id: script.id,
              name: script.name || script.title || `Script ${script.id}`,
            })),
          }],
        };
      });

      // Show confirmation modal instead of toast
      setAssignmentData(confirmationData);
      setShowConfirmationModal(true);
      
      if (onAssignComplete) {
        onAssignComplete(assignments);
      }

    } catch (error) {
      console.error('Error assigning creators:', error);
      setToast('Error assigning creators. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="assign-creator-modal-overlay-unique">
      <div className="assign-creator-modal-container-unique">
        {/* Header */}
        <div className="assign-creator-modal-header-unique">
          <button className="assign-creator-modal-close-btn-unique" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
          <h2>Assign Creators</h2>
          <p>Assign {creators.length} creator{creators.length !== 1 ? 's' : ''} to your campaign</p>
        </div>

        {/* Progress Indicator - Horizontal */}
        <div className="assign-creator-progress-unique">
          <div className="assign-creator-steps-container-unique">
            {steps.map((step, index) => (
              <div key={step.number} className={`assign-creator-step-unique ${currentStep >= step.number ? 'active' : ''} ${currentStep === step.number ? 'current' : ''}`}>
                <div className="assign-creator-step-number-unique">
                  {currentStep > step.number ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    step.number
                  )}
                </div>
                <div className="assign-creator-step-info-unique">
                  <span className="assign-creator-step-title-unique">{step.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="assign-creator-modal-content-unique">
          {/* Step 1: Campaign Selection */}
          {currentStep === 1 && (
            <div className="assign-creator-step-content-unique">
              <h3>Select Campaign</h3>
              <p>Choose which campaign you want to assign these creators to.</p>
              <div className="assign-creator-campaign-grid-unique">
                {campaignsWithBriefs.length > 0 ? (
                  campaignsWithBriefs.map(campaign => (
                    <div
                      key={campaign.id}
                      className={`assign-creator-campaign-card-unique ${selectedCampaign === campaign.id ? 'selected' : ''}`}
                      onClick={() => setSelectedCampaign(campaign.id)}
                    >
                    <div className="assign-creator-campaign-avatar-unique">
                      {campaign.avatarImage ? (
                        <img src={campaign.avatarImage} alt={campaign.name} />
                      ) : (
                        <div 
                          className="assign-creator-campaign-avatar-placeholder-unique"
                          style={{ 
                            background: campaign.avatarColor?.includes('gradient') ? campaign.avatarColor : undefined,
                            backgroundColor: campaign.avatarColor?.includes('gradient') ? undefined : (campaign.avatarColor || '#f8049c')
                          }}
                        >
                          {campaign.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                      <h4>{campaign.name}</h4>
                      {campaign.description && <p>{campaign.description}</p>}
                    </div>
                  ))
                ) : (
                  <div className="assign-creator-no-campaigns-unique">
                    <p>No campaigns with briefs found. Please create a campaign and add briefs first.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Brief Selection */}
          {currentStep === 2 && (
            <div className="assign-creator-step-content-unique">
              <h3>Choose Briefs</h3>
              <p>Select which brief each creator will work on.</p>
              <div className="assign-creator-creators-list-unique">
                {displayCreators.map(creator => (
                  <div key={creator.handle} className="assign-creator-creator-row-unique">
                    <div className="assign-creator-creator-info-unique">
                      <img src={creator.profileImage || creator.image} alt={creator.handle} />
                      <div>
                        <h4>{creator.name}</h4>
                        <span>{formatHandleForDisplay(creator.handle)}</span>
                      </div>
                    </div>
                    <div className="assign-creator-brief-selector-unique">
                      <select
                        value={selectedBriefs[creator.handle] || ''}
                        onChange={(e) => handleBriefChange(creator.handle, e.target.value)}
                      >
                        <option value="">Select a brief...</option>
                        {briefs.map(brief => (
                          <option key={brief.id} value={brief.id}>{brief.title}</option>
                        ))}
                      </select>
                    </div>
                    <button 
                      className="assign-creator-remove-btn-unique"
                      onClick={() => handleRemoveCreator(creator.handle)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Script Selection */}
          {currentStep === 3 && (
            <div className="assign-creator-step-content-unique">
              <h3>Pick Scripts</h3>
              <p>Choose which script each creator will work on under their selected brief.</p>
              <div className="assign-creator-scripts-list-unique">
                {displayCreators.map(creator => {
                  const selectedBriefId = selectedBriefs[creator.handle];
                  const briefScripts = getBriefScripts(selectedBriefId); // Already filtered to exclude drafts
                  const selectedBrief = briefs.find(b => b.id === selectedBriefId);
                  
                  // Check if there are any non-draft scripts available
                  if (!selectedBriefId || selectedBriefId === '' || briefScripts.length === 0) {
                    return null;
                  }

                  return (
                    <div key={creator.handle} className="assign-creator-script-section-unique">
                      <div className="assign-creator-script-header-unique">
                        <div className="assign-creator-creator-info-unique">
                          <img src={creator.profileImage || creator.image} alt={creator.handle} />
                          <div>
                            <h4>{creator.name}</h4>
                            <span>{formatHandleForDisplay(creator.handle)}</span>
                          </div>
                        </div>
                        <div className="assign-creator-brief-info-unique">
                          <span>Brief: <strong>{selectedBrief?.title}</strong></span>
                        </div>
                      </div>
                      <div className="assign-creator-script-selector-unique">
                        {/* Selected Scripts */}
                        {selectedScripts[creator.handle]?.[selectedBriefId]?.map(scriptId => {
                          const script = briefScripts.find(s => s.id === scriptId);
                          return script ? (
                            <div key={scriptId} className="assign-creator-selected-script-unique">
                              <span>{script.name || script.title || `Script ${script.id}`}</span>
                              <button 
                                type="button"
                                className="assign-creator-remove-script-btn-unique"
                                onClick={() => handleRemoveScript(creator.handle, selectedBriefId, scriptId)}
                              >
                                ×
                              </button>
                            </div>
                          ) : null;
                        })}
                        
                        {/* Add Script Dropdown */}
                        <div className="assign-creator-add-script-unique">
                          <select
                            value={scriptDropdownValues[creator.handle]?.[selectedBriefId] || ''}
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              if (selectedValue) {
                                // Convert string to number since script IDs are numbers
                                const scriptId = parseInt(selectedValue, 10);
                                handleScriptChange(creator.handle, selectedBriefId, scriptId);
                              }
                            }}
                          >
                            <option value="">Add a script...</option>
                            {briefScripts
                              .filter(script => 
                                !selectedScripts[creator.handle]?.[selectedBriefId]?.includes(script.id)
                                // Note: isDraft filter is already applied in getBriefScripts
                              )
                              .map(script => (
                                <option key={script.id} value={script.id.toString()}>
                                  {script.name || script.title || `Script ${script.id}`}
                                </option>
                              ))}
                          </select>
                          {briefScripts.length === 0 && (() => {
                            // Check if there are any scripts (including drafts) in the brief
                            const allBriefScripts = selectedBrief?.scripts || [];
                            return allBriefScripts.length > 0 && allBriefScripts.every(s => s.isDraft);
                          })() && (
                            <div className="assign-creator-draft-notice-unique">
                              <span className="draft-icon">📝</span>
                              Only draft scripts available. Complete a script to assign it to creators.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Review & Assign */}
          {currentStep === 4 && (
            <div className="assign-creator-step-content-unique">
              <h3>Review & Assign</h3>
              <p>Review your selections and pricing breakdown before assigning the creators.</p>
              <div className="assign-creator-review-list-unique">
                {creators.map(creator => {
                  const selectedBriefId = selectedBriefs[creator.handle];
                  const selectedScriptIds = selectedScripts[creator.handle]?.[selectedBriefId];
                  const selectedBrief = briefs.find(b => b.id === selectedBriefId);
                  const assignedScripts = selectedBrief?.scripts?.filter(s => selectedScriptIds?.includes(s.id)) || [];
                  
                  return (
                    <div key={creator.handle} className="assign-creator-review-item-unique">
                      <div className="assign-creator-creator-info-unique">
                        <img src={creator.profileImage || creator.image} alt={creator.handle} />
                        <div>
                          <div className="assign-creator-name-row-unique">
                            <h4>{creator.name}</h4>
                            {creator.tier && (
                              <div className={`assign-creator-badge-inline-unique ${creator.tier === 'Pro' ? 'pro-badge-unique' : 'rising-star-badge-unique'}`}>
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                  <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
                                </svg>
                                {creator.tier === 'Pro' ? 'Pro' : 'Rising Star'}
                              </div>
                            )}
                          </div>
                          <span>{formatHandleForDisplay(creator.handle)}</span>
                        </div>
                      </div>
                      <div className="assign-creator-assignment-details-unique">
                        <div className="assign-creator-assignment-item-unique">
                          <span className="assign-creator-assignment-label-unique">Campaign:</span>
                          <span className="assign-creator-assignment-value-unique">{selectedCampaign}</span>
                        </div>
                        {selectedBrief && (
                          <div className="assign-creator-assignment-item-unique">
                            <span className="assign-creator-assignment-label-unique">Brief:</span>
                            <span className="assign-creator-assignment-value-unique">{selectedBrief.title}</span>
                            <button 
                              className="assign-creator-review-btn-unique"
                              onClick={(e) => {
                                e.stopPropagation();
                                setReviewContent(selectedBrief);
                                setReviewType('brief');
                                setShowReviewModal(true);
                              }}
                            >
                              Review Brief
                            </button>
                          </div>
                        )}
                        {assignedScripts.length > 0 && (
                          <div className="assign-creator-scripts-section-unique">
                            <div className="assign-creator-scripts-header-unique">
                              <span className="assign-creator-assignment-label-unique">Scripts:</span>
                            </div>
                            <div className="assign-creator-scripts-list-unique">
                              {assignedScripts.map(script => (
                                <div key={script.id} className="assign-creator-script-item-unique">
                                  <span className="assign-creator-script-name-unique">{script.name || script.title}</span>
                                  <button 
                                    className="assign-creator-review-btn-unique"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setReviewContent(script);
                                      setReviewType('script');
                                      setShowReviewModal(true);
                                    }}
                                  >
                                    Review Script
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {assignedScripts.length > 0 && (
                          <div className="assign-creator-pricing-breakdown-unique">
                            <div className="assign-creator-pricing-header-unique">
                              <span className="assign-creator-pricing-label-unique">Pricing Breakdown:</span>
                            </div>
                            <div className="assign-creator-pricing-details-unique">
                              {assignedScripts.map(script => {
                                const pricing = calculateScriptPricing(script, creator.tier);
                                return (
                                  <div key={script.id} className="assign-creator-script-pricing-unique">
                                    <div className="assign-creator-script-pricing-header-unique">
                                      <span>{script.name || script.title}</span>
                                    </div>
                                    <div className="assign-creator-pricing-line-unique">
                                      <span>Base Script ({creator.tier}):</span>
                                      <span className="assign-creator-pricing-value-unique">{pricing.basePrice} credit{pricing.basePrice !== 1 ? 's' : ''}</span>
                                    </div>
                                    {(pricing.extraHooks > 0 || pricing.extraCTAs > 0) && (
                                      <>
                                        {pricing.extraHooks > 0 && (
                                          <div className="assign-creator-pricing-line-unique">
                                            <span>+{pricing.extraHooks} Additional Hook{pricing.extraHooks > 1 ? 's' : ''}:</span>
                                            <span className="assign-creator-pricing-value-unique">+{pricing.extraHooks * 0.25} credits</span>
                                          </div>
                                        )}
                                        {pricing.extraCTAs > 0 && (
                                          <div className="assign-creator-pricing-line-unique">
                                            <span>+{pricing.extraCTAs} Additional CTA{pricing.extraCTAs > 1 ? 's' : ''}:</span>
                                            <span className="assign-creator-pricing-value-unique">+{pricing.extraCTAs * 0.25} credits</span>
                                          </div>
                                        )}
                                      </>
                                    )}
                                    <div className="assign-creator-pricing-line-unique">
                                      <span>Script Total:</span>
                                      <span className="assign-creator-pricing-value-unique">{pricing.total.toFixed(2)} credits</span>
                                    </div>
                                  </div>
                                );
                              })}
                              <div className="assign-creator-pricing-total-unique">
                                <span>Subtotal:</span>
                                <span className="assign-creator-pricing-total-value-unique">
                                  {assignedScripts.reduce((total, script) => total + calculateScriptPricing(script, creator.tier).total, 0).toFixed(2)} credits
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Total Pricing Summary */}
              {(() => {
                const { totalCredits, totalExtras } = calculateTotalPricing();
                return totalCredits > 0 ? (
                  <div className="assign-creator-total-summary-unique">
                    <div className="assign-creator-total-header-unique">
                      <h4>Total Assignment Cost</h4>
                    </div>
                    <div className="assign-creator-total-breakdown-unique">
                      <div className="assign-creator-total-line-unique">
                        <span>Base Scripts:</span>
                        <span className="assign-creator-total-value-unique">{(totalCredits - totalExtras).toFixed(2)} credits</span>
                      </div>
                      {totalExtras > 0 && (
                        <div className="assign-creator-total-line-unique">
                          <span>Additional Hooks & CTAs:</span>
                          <span className="assign-creator-total-value-unique">+{totalExtras.toFixed(2)} credits</span>
                        </div>
                      )}
                      <div className="assign-creator-total-final-unique">
                        <span>Total:</span>
                        <span className="assign-creator-total-final-value-unique">{totalCredits.toFixed(2)} credits</span>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="assign-creator-modal-footer-unique">
          <div className="assign-creator-modal-actions-unique">
            {currentStep > 1 && (
              <button className="assign-creator-btn-unique assign-creator-btn-secondary-unique" onClick={handleBack}>
                Back
              </button>
            )}
            {currentStep < 4 ? (
              <button 
                className="assign-creator-btn-unique assign-creator-btn-primary-unique" 
                onClick={handleNext}
                disabled={!canProceed()}
              >
                Next
              </button>
            ) : (
              <button 
                className="assign-creator-btn-unique assign-creator-btn-primary-unique" 
                onClick={handleFinalAssign}
                disabled={isLoading}
              >
                {isLoading ? 'Assigning...' : 'Assign Creators'}
              </button>
            )}
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div className="assign-creator-toast-unique">
            {toast}
          </div>
        )}
      </div>

      {/* Review Content Modal */}
      <ReviewContentModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setReviewContent(null);
          setReviewType(null);
        }}
        type={reviewType}
        content={reviewContent}
      />

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        isOpen={showCreditPurchaseModal}
        onClose={() => setShowCreditPurchaseModal(false)}
        currentCredits={currentCredits}
        onPurchaseSuccess={async () => {
          setShowCreditPurchaseModal(false);
          // Refresh credits after purchase
          try {
            const updatedCredits = await CreditService.getBalance();
            setCurrentCredits(updatedCredits);
          } catch (error) {
            console.error('Error refreshing credits:', error);
          }
        }}
      />

      {/* Creator Assignment Confirmation Modal */}
      <CreatorAssignmentConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => {
          setShowConfirmationModal(false);
          onClose();
        }}
        assignments={assignmentData}
        onBackToCreatorList={() => {
          setShowConfirmationModal(false);
          onClose();
        }}
      />
    </div>
  );
}

export default AssignCreatorModal;


